<template>
  <b-card
    no-body
    class="mt-2"
  >
    <b-card-body>
      <b-row>
        <b-col>
          <b-card-title v-text="$t('guides.steps.title')" />
        </b-col>
        <b-col>
          <div class="text-right">
            <b-button
              variant="outline-secondary"
              class="mr-1"
              @click="saveOrdering"
            >
              <feather-icon icon="SaveIcon" /> {{ $t('guides.steps.saveOrdering') }}
            </b-button>
            <b-button
              variant="primary"
              @click="addRow"
            >
              <FeatherIcon icon="PlusIcon" /> {{ $t('common.add') }}
            </b-button>
          </div>
        </b-col>
      </b-row>
    </b-card-body>
    <template v-if="dataLoading">
      <loading />
    </template>
    <template v-else>
      <draggable
        v-model="stepsList"
        handle=".drag-handle"
        @end="onDragEnd"
      >
        <div
          v-for="(step, index) in stepsList"
          :key="step.id || `new-${index}`"
          class="guide-step-row d-flex align-items-start p-2 border-bottom"
        >
          <div class="drag-handle mr-2 mt-2 cursor-grab">
            <feather-icon icon="MenuIcon" />
          </div>
          <div class="flex-grow-1">
            <b-form-input
              v-model="step.title"
              :placeholder="$t('guides.steps.stepTitlePlaceholder')"
              class="mb-2"
            />
            <b-collapse
              :id="`desc-${step.id || index}`"
              :visible="!!(step.description && String(step.description).trim())"
            >
              <b-form-textarea
                v-model="step.description"
                :placeholder="$t('guides.steps.stepDescriptionPlaceholder')"
                rows="2"
              />
            </b-collapse>
            <b-button
              v-b-toggle="`desc-${step.id || index}`"
              variant="link"
              size="sm"
              class="p-0"
            >
              {{ step.description ? $t('guides.steps.editDescription') : $t('guides.steps.addDescription') }}
            </b-button>
          </div>
          <div class="ml-2">
            <b-button
              variant="success"
              size="sm"
              class="mr-1"
              @click="saveRow(step)"
            >
              <feather-icon icon="SaveIcon" />
            </b-button>
            <b-button
              variant="danger"
              size="sm"
              @click="removeStep(step, index)"
            >
              <feather-icon icon="TrashIcon" />
            </b-button>
          </div>
        </div>
      </draggable>
      <b-card-footer
        v-if="stepsList.length === 0"
        class="text-muted"
      >
        {{ $t('guides.steps.empty', { add: $t('common.add') }) }}
      </b-card-footer>
    </template>
  </b-card>
</template>

<script>
import draggable from 'vuedraggable'
import {
  BCard, BCardBody, BCardTitle, BCardFooter, BRow, BCol, BButton, BFormInput, BFormTextarea, BCollapse, VBToggle,
} from 'bootstrap-vue'
import Loading from '@/layouts/components/common/Loading.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'GuideSteps',
  directives: {
    'b-toggle': VBToggle,
  },
  components: {
    draggable,
    Loading,
    BCard,
    BCardBody,
    BCardTitle,
    BCardFooter,
    BRow,
    BCol,
    BButton,
    BFormInput,
    BFormTextarea,
    BCollapse,
  },
  computed: {
    stepsList: {
      get() {
        return this.$store.getters['guideSteps/dataList'] || []
      },
      set(val) {
        this.$store.commit('guideSteps/SET_DATA_LIST', val || [])
      },
    },
    guide() {
      return this.$store.getters['guides/dataItem']
    },
    dataLoading() {
      return this.$store.getters['guideSteps/loading']
    },
    saveData() {
      return this.$store.getters['guideSteps/dataSaveStatus']
    },
  },
  watch: {
    saveData(val) {
      if (val?.status === true) {
        this.showToast({
          variant: 'success', title: this.$t('common.toast.success'), icon: 'CheckIcon', text: val.message,
        })
        this.getDataList()
      } else if (val?.status === false) {
        this.showToast({
          variant: 'danger', title: this.$t('common.toast.error'), icon: 'XIcon', text: val.message,
        })
      }
    },
  },
  created() {
    this.getDataList()
  },
  methods: {
    getDataList() {
      this.$store.dispatch('guideSteps/getDataList', {
        select: [
          'guide_steps.id AS id',
          'guide_steps.id_guides AS id_guides',
          'guide_steps.title AS title',
          'guide_steps.description AS description',
          'guide_steps.ordering AS ordering',
        ],
        where: { 'guide_steps.id_guides': this.guide.id },
        limit: 500,
        start: 0,
      })
    },
    addRow() {
      const newStep = {
        id_guides: this.guide.id,
        title: '',
        description: '',
        ordering: this.stepsList.length,
      }
      this.stepsList = [...this.stepsList, newStep]
    },
    saveRow(step) {
      if (!step.title || !step.title.trim()) {
        this.showToast({
          title: this.$t('common.toast.warning'), icon: 'XIcon', text: this.$t('guides.steps.titleRequired'), variant: 'danger',
        })
        return
      }
      const payload = {
        id_guides: this.guide.id,
        title: step.title.trim(),
        description: step.description || null,
        ordering: step.ordering,
      }
      if (step.id) payload.id = step.id
      this.$store.dispatch('guideSteps/saveData', payload)
    },
    onDragEnd() {
      this.stepsList = this.stepsList.map((s, i) => ({ ...s, ordering: i }))
    },
    saveOrdering() {
      const stepsWithId = this.stepsList.filter(s => s.id)
      if (stepsWithId.length === 0) return
      const promises = stepsWithId.map(s => this.$store.dispatch('guideSteps/saveData', {
        id: s.id,
        id_guides: this.guide.id,
        title: s.title,
        description: s.description,
        ordering: s.ordering,
      }))
      Promise.all(promises).then(() => {
        this.showToast({
          variant: 'success', title: this.$t('guides.steps.orderingSaved'), icon: 'CheckIcon', text: '',
        })
        this.getDataList()
      })
    },
    removeStep(step, index) {
      if (step.id) {
        this.$swal({
          title: this.$t('alerts.remove.title'),
          text: this.$t('alerts.remove.message'),
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: this.$t('alerts.remove.confirm'),
          cancelButtonText: this.$t('alerts.remove.cancel'),
          customClass: { confirmButton: 'btn btn-success', cancelButton: 'btn btn-danger ml-1' },
          buttonsStyling: false,
        }).then(result => {
          if (result.value) {
            this.$store.dispatch('guideSteps/removeData', step.id).then(() => {
              this.getDataList()
              this.$swal({
                icon: 'success',
                title: this.$t('alerts.remove.resultTitle'),
                text: this.$t('alerts.remove.resultMessage'),
                confirmButtonText: this.$t('alerts.remove.resultClose'),
                customClass: { confirmButton: 'btn btn-success' },
              })
            })
          }
        })
      } else {
        this.stepsList = this.stepsList.filter((_, i) => i !== index)
      }
    },
    showToast(params) {
      this.$toast({ component: ToastificationContent, props: params })
    },
  },
}
</script>

<style scoped>
.guide-step-row:hover {
  background-color: rgba(0, 0, 0, 0.02);
}
.cursor-grab {
  cursor: grab;
}
.cursor-grab:active {
  cursor: grabbing;
}
</style>
